import { useState } from 'react';
import {
  Copy,
  Check,
  MapPin,
  Building2,
  Loader2,
  AlertCircle,
  Package,
  Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import { useLocations } from '@/hooks/useLocations';
import {
  useCopyCategoryToLocations,
  type CopyResult,
} from '@/hooks/useCopyCategoryToLocations';

interface CopyCategoriesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceLocationId: string;
  sourceLocationName: string;
  selectedCategoryIds?: string[];
  categoryCount?: number;
}

export function CopyCategoriesModal({
  open,
  onOpenChange,
  sourceLocationId,
  sourceLocationName,
  selectedCategoryIds,
  categoryCount,
}: CopyCategoriesModalProps) {
  const { locations } = useLocations();
  const copyMutation = useCopyCategoryToLocations();

  const [targetLocationIds, setTargetLocationIds] = useState<string[]>([]);
  const [includeProducts, setIncludeProducts] = useState(false);
  const [locationPopoverOpen, setLocationPopoverOpen] = useState(false);
  const [result, setResult] = useState<CopyResult | null>(null);

  // Filter out source location from available targets
  const availableTargets = locations.filter((loc) => loc.id !== sourceLocationId);

  const allSelected =
    targetLocationIds.length === availableTargets.length && availableTargets.length > 0;

  const handleSelectAll = () => {
    if (allSelected) {
      setTargetLocationIds([]);
    } else {
      setTargetLocationIds(availableTargets.map((l) => l.id));
    }
  };

  const handleToggleLocation = (locationId: string) => {
    if (targetLocationIds.includes(locationId)) {
      setTargetLocationIds(targetLocationIds.filter((id) => id !== locationId));
    } else {
      setTargetLocationIds([...targetLocationIds, locationId]);
    }
  };

  const getDisplayText = () => {
    if (targetLocationIds.length === 0) {
      return 'Select target locations';
    }
    if (allSelected) {
      return 'All locations';
    }
    if (targetLocationIds.length === 1) {
      const loc = availableTargets.find((l) => l.id === targetLocationIds[0]);
      return loc?.name || '1 location';
    }
    return `${targetLocationIds.length} locations`;
  };

  const handleCopy = async () => {
    setResult(null);
    try {
      const copyResult = await copyMutation.mutateAsync({
        sourceLocationId,
        targetLocationIds,
        includeProducts,
        categoryIds: selectedCategoryIds || null,
      });
      setResult(copyResult);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const handleClose = () => {
    setTargetLocationIds([]);
    setIncludeProducts(false);
    setResult(null);
    onOpenChange(false);
  };

  const copying = copyMutation.isPending;
  const hasError = copyMutation.isError;

  return (
    <ResponsiveModal open={open} onOpenChange={handleClose}>
      <ResponsiveModalContent maxWidth="md">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Copy Categories to Locations
          </ResponsiveModalTitle>
          <ResponsiveModalDescription>
            Copy {selectedCategoryIds?.length || categoryCount || 'all'} categories from{' '}
            {sourceLocationName} to other locations
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="space-y-4">
          {/* Source location display */}
          <div className="p-3 bg-muted rounded-lg">
            <Label className="text-xs text-muted-foreground">Source Location</Label>
            <div className="flex items-center gap-2 mt-1">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{sourceLocationName}</span>
            </div>
          </div>

          {/* Target locations selector */}
          <div className="space-y-2">
            <Label>Target Locations</Label>
            {availableTargets.length === 0 ? (
              <div className="p-3 border border-dashed rounded-lg text-center text-sm text-muted-foreground">
                <AlertCircle className="h-4 w-4 mx-auto mb-1" />
                No other locations available
              </div>
            ) : (
              <>
                <Popover open={locationPopoverOpen} onOpenChange={setLocationPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={locationPopoverOpen}
                      className="w-full justify-between"
                      disabled={copying}
                    >
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span
                          className={targetLocationIds.length === 0 ? 'text-muted-foreground' : ''}
                        >
                          {getDisplayText()}
                        </span>
                      </div>
                      {targetLocationIds.length > 0 && (
                        <Badge variant="secondary" className="ml-2">
                          {targetLocationIds.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <div className="p-2 border-b">
                      <div
                        className={cn(
                          'flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50',
                          allSelected && 'bg-primary/10'
                        )}
                        onClick={handleSelectAll}
                      >
                        <Checkbox
                          checked={allSelected}
                          className="data-[state=checked]:bg-primary"
                        />
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">All Locations</span>
                        {allSelected && <Check className="h-4 w-4 ml-auto text-primary" />}
                      </div>
                    </div>
                    <div className="max-h-[200px] overflow-y-auto p-2">
                      {availableTargets.map((location) => {
                        const isSelected = targetLocationIds.includes(location.id);
                        return (
                          <div
                            key={location.id}
                            className={cn(
                              'flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50',
                              isSelected && 'bg-primary/5'
                            )}
                            onClick={() => handleToggleLocation(location.id)}
                          >
                            <Checkbox
                              checked={isSelected}
                              className="data-[state=checked]:bg-primary"
                            />
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span>{location.name}</span>
                            {isSelected && <Check className="h-4 w-4 ml-auto text-primary" />}
                          </div>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Selected locations badges */}
                {targetLocationIds.length > 0 && targetLocationIds.length < availableTargets.length && (
                  <div className="flex flex-wrap gap-1">
                    {targetLocationIds.slice(0, 3).map((locId) => {
                      const loc = availableTargets.find((l) => l.id === locId);
                      return (
                        <Badge key={locId} variant="secondary" className="text-xs">
                          {loc?.name}
                        </Badge>
                      );
                    })}
                    {targetLocationIds.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{targetLocationIds.length - 3} more
                      </Badge>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Include products toggle */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <Package className="h-5 w-5 text-muted-foreground" />
              <div>
                <Label htmlFor="include-products" className="cursor-pointer">
                  Include Products
                </Label>
                <p className="text-xs text-muted-foreground">
                  Copy products within each category to target locations
                </p>
              </div>
            </div>
            <Switch
              id="include-products"
              checked={includeProducts}
              onCheckedChange={setIncludeProducts}
              disabled={copying}
            />
          </div>

          {/* Result display */}
          {result && (
            <div
              className={cn(
                'p-4 rounded-lg border',
                result.errors.length > 0
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-green-50 border-green-200'
              )}
            >
              <div className="flex items-start gap-3">
                <Check
                  className={cn(
                    'h-5 w-5 mt-0.5',
                    result.errors.length > 0 ? 'text-amber-600' : 'text-green-600'
                  )}
                />
                <div className="space-y-2 flex-1">
                  <p
                    className={cn(
                      'font-medium',
                      result.errors.length > 0 ? 'text-amber-800' : 'text-green-800'
                    )}
                  >
                    Copy operation completed
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-muted-foreground" />
                      <span>
                        Categories: {result.categories_copied} copied
                        {result.categories_skipped > 0 && (
                          <span className="text-muted-foreground">
                            , {result.categories_skipped} skipped
                          </span>
                        )}
                      </span>
                    </div>
                    {includeProducts && (
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span>
                          Products: {result.products_copied} copied
                          {result.products_skipped > 0 && (
                            <span className="text-muted-foreground">
                              , {result.products_skipped} skipped
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  {result.errors.length > 0 && (
                    <div className="text-xs text-amber-700 mt-2">
                      {result.errors.map((err, i) => (
                        <p key={i}>{err}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Error display */}
          {hasError && (
            <div className="p-4 rounded-lg border bg-red-50 border-red-200">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">Failed to copy categories</span>
              </div>
              <p className="text-sm text-red-600 mt-1">
                {copyMutation.error?.message || 'An unexpected error occurred'}
              </p>
            </div>
          )}
        </ResponsiveModalBody>

        <ResponsiveModalFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={copying}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              variant="blaze"
              onClick={handleCopy}
              disabled={copying || targetLocationIds.length === 0}
            >
              {copying && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Copy Categories
            </Button>
          )}
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
